/**
 * SearchSelect — generic, theme-native searchable single-select.
 *
 * Extracted from the AuthorSelectButton / ParentSelectButton pattern
 * so any form can have a token-styled, dark-mode-correct typeahead
 * without reaching for MUI's <Autocomplete> (whose theme is hardwired
 * to light app-wide). Trigger button + absolute popover with a search
 * field and a filtered option list; click-outside / Escape close it.
 *
 * Options are plain `{ value, label, sublabel? }`. `value` is the
 * primitive (id / code) — the caller stores and receives that, not an
 * option object.
 *
 *   <SearchSelect
 *     value={challengeId}
 *     onChange={setChallengeId}
 *     options={challengeOptions}
 *     placeholder="Search challenges…"
 *   />
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../cn';

// Gap between trigger and dropdown, and the margin kept to the viewport edge.
const GAP = 6;
const EDGE = 8;
// Search header, list padding and borders (~52px) — the part that is not the list.
const CHROME = 56;
const LIST_MAX = 320;

export default function SearchSelect({
  value = null,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  clearable = true,
  disabled = false,
  maxRender = 100,
  id,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rect, setRect] = useState(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // The dropdown renders in a portal on <body> so an ancestor card's
  // `overflow: hidden` can't clip it. Position it (fixed) from the
  // trigger's bounding rect, and keep it pinned on scroll / resize.
  const updateRect = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Only re-render when the trigger actually moved. The capture-phase
    // scroll listener also fires for scrolling inside the dropdown's
    // own options list (it's a descendant in the portal) — in that case
    // the trigger is stationary, so returning the previous rect lets
    // React bail out instead of re-rendering all the option rows.
    // The viewport height is kept too: it decides whether the dropdown
    // opens up or down.
    const vh = window.innerHeight;
    setRect((prev) =>
      prev && prev.top === r.top && prev.left === r.left && prev.width === r.width
        && prev.vh === vh
        ? prev
        : { top: r.top, bottom: r.bottom, left: r.left, width: r.width, vh },
    );
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    updateRect();
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    // `true` (capture) so we also catch scrolling inside ancestor
    // containers, not just the window.
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  // Click-outside (trigger or portaled dropdown) + Escape close.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  // Cap how many rows hit the DOM at once — large option sets (e.g.
  // thousands of sessions, each with a thumbnail) would otherwise lag
  // when the dropdown opens on an empty query. Hidden matches surface
  // as the user narrows the search.
  const shown = useMemo(
    () => (maxRender > 0 ? filtered.slice(0, maxRender) : filtered),
    [filtered, maxRender],
  );
  const hiddenCount = filtered.length - shown.length;

  // Open downward by default. When the trigger sits near the bottom of
  // the viewport and there is more room above, open upward (anchored by
  // `bottom`, so it grows away from the trigger). Either way, cap the
  // list to the space it has, with no floor: a floor would push an
  // upward dropdown's search field off the top of a short window.
  let placement = null;
  if (rect) {
    const below = rect.vh - rect.bottom - GAP - EDGE;
    const above = rect.top - GAP - EDGE;
    const up = below < CHROME + LIST_MAX && above > below;
    const space = up ? above : below;
    placement = {
      position: up
        ? { bottom: rect.vh - rect.top + GAP }
        : { top: rect.bottom + GAP },
      listMax: Math.max(0, Math.min(LIST_MAX, space - CHROME)),
    };
  }

  return (
    <div ref={wrapperRef} className={cn('relative', className)} style={{ minWidth: 0 }}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="flex items-center w-full transition-all duration-150"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 12px',
          background: 'rgb(var(--color-cream))',
          border: '1px solid rgb(var(--color-sand))',
          borderRadius: 8,
          textAlign: 'left',
          fontFamily: 'inherit',
          fontSize: 13.5,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
        }}
      >
        {selected && selected.image && (
          <span
            aria-hidden
            className="shrink-0"
            style={{
              display: 'inline-block',
              verticalAlign: 'middle',
              width: 22,
              height: 22,
              borderRadius: 5,
              background: 'rgb(var(--color-cream))',
              backgroundImage: `url("${selected.image}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              border: '1px solid rgb(var(--color-sand))',
            }}
          />
        )}
        <span className="flex-1 min-w-0 block truncate">
          {selected ? (
            <span className="text-navy">{selected.label}</span>
          ) : (
            <span className="text-text-muted">{placeholder}</span>
          )}
        </span>
        {clearable && selected && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear selection"
            className="text-text-muted shrink-0"
            style={{ fontSize: 14, lineHeight: 1, padding: '0 2px' }}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
          >
            ×
          </span>
        )}
        <span className="text-text-muted shrink-0" style={{ fontSize: 11 }}>▾</span>
      </button>

      {open && placement && createPortal(
        <div
          ref={dropdownRef}
          data-testid="search-select-dropdown"
          style={{
            position: 'fixed',
            ...placement.position,
            left: rect.left,
            width: rect.width,
            background: 'rgb(var(--color-surface))',
            border: '1px solid rgb(var(--color-sand))',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgb(var(--color-navy) / 0.12)',
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          {/*
            Note: everything below is portaled onto document.body (outside
            #root), and Tailwind's `important: '#root'` config generates every
            utility as `#root .foo{...}` — a selector that can never match a
            portaled subtree. Utility classNames here are silent no-ops, so
            layout/color/truncation must be inline styles, not classNames
            (see the flex-row fix a few lines down for the row this was
            first discovered on).
          */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid rgb(var(--color-sand))',
              background: 'rgb(var(--color-cream))',
            }}
          >
            <svg
              aria-hidden
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'rgb(var(--color-text-muted))', flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                color: 'rgb(var(--color-navy))',
              }}
            />
          </div>

          <div style={{ maxHeight: placement.listMax, overflowY: 'auto', padding: '6px 0' }}>
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: '12px 14px',
                  fontSize: 13,
                  color: 'rgb(var(--color-text-muted))',
                  fontStyle: 'italic',
                }}
              >
                {emptyText}
              </div>
            ) : (
              shown.map((o) => {
                const isActive = o.value === value;
                return (
                  <button
                    key={String(o.value)}
                    type="button"
                    onClick={() => {
                      onChange?.(o.value);
                      setOpen(false);
                    }}
                    style={{
                      // Everything here is inline style, not a utility className —
                      // this row is inside the document.body portal above, where
                      // Tailwind's `#root`-scoped utilities never match (see note
                      // above the search header).
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      cursor: 'pointer',
                      gap: 10,
                      padding: '8px 14px',
                      fontSize: 13,
                      color: isActive
                        ? 'rgb(var(--color-teal-green-dark))'
                        : 'rgb(var(--color-navy))',
                      fontWeight: isActive ? 600 : 400,
                      background: isActive ? 'var(--color-teal-green-soft)' : 'transparent',
                      border: 0,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      transition: 'background 120ms ease',
                    }}
                    onMouseEnter={(e) => {
                      if (isActive) return;
                      e.currentTarget.style.background = 'rgb(var(--color-cream))';
                    }}
                    onMouseLeave={(e) => {
                      if (isActive) return;
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {o.image && (
                      <span
                        aria-hidden
                        style={{
                          // Explicit display so width/height apply even if the
                          // row isn't a flex container (a bare inline <span>
                          // ignores height — the thumbnail would collapse).
                          display: 'inline-block',
                          verticalAlign: 'middle',
                          flexShrink: 0,
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          background: 'rgb(var(--color-cream))',
                          backgroundImage: o.image ? `url("${o.image}")` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          border: '1px solid rgb(var(--color-sand))',
                        }}
                      />
                    )}
                    <span style={{ flex: '1 1 0%', minWidth: 0, textAlign: 'left' }}>
                      <span
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {o.label}
                      </span>
                      {o.sublabel && (
                        <span
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'rgb(var(--color-text-muted))',
                            fontSize: 11,
                            marginTop: 1,
                            fontWeight: 400,
                          }}
                        >
                          {o.sublabel}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
            {hiddenCount > 0 && (
              <div
                style={{
                  padding: '8px 14px',
                  fontSize: 11.5,
                  borderTop: '1px solid rgb(var(--color-sand))',
                  color: 'rgb(var(--color-text-muted))',
                  fontStyle: 'italic',
                }}
              >
                +{hiddenCount} more — refine your search
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
